import React, { useEffect, useState, useRef } from 'react';
import { Button, message } from 'antd';
import TagListInput from '../../components/TagListInput';
import { StyledModal } from './styles';
import { COMPLETE_TRANSACTION } from '../ComplexDocumentDesign/translationValues';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import TransactionCompleteModal from '../../components/Modals/TransactionCompleteModal';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { fetchRecipients, sendInteractiveEmail, updateRecipients } from '../../utils/services/interactiveDesignApi';
import { SEND_ID_EMAIL } from './translationValues';
import { SEND_EMAIL_MODAL } from '../../components/Modals/translationValues';
import { PdsClientEventName } from '../../../shared';
import { parentWindow } from '../../utils/embedHelper';
import { useEmbedding } from '../../context/embedContext';

const NAMESPACES = ['COMPLETE_TRANSACTION', 'SEND_ID_EMAIL'];

const SendIdEmail = ({ docId, visible, onCancel, onConfirm }) => {
    const { t } = useTranslation(NAMESPACES);
    const [originalTo, setOriginalTo] = useState([]);
    const [originalCc, setOriginalCc] = useState([]);
    const [toAddresses, setToAddresses] = useState([]);
    const [ccAddresses, setCcAddresses] = useState([]);
    const [loadingTo, setLoadingTo] = useState(false);
    const [loadingCc, setLoadingCc] = useState(false);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [showTransacModal, setShowTransacModal] = useState(false);

    const history = useHistory();
    const { embedState } = useEmbedding();

    const fetchEmailAddresses = async () => {
        const payload = { interactiveDesignId: docId };
        try {
            const response = await fetchRecipients(payload);
            if (response.statusCode === 5506) {
                const toAddresses = response.data.interactiveToAddresses
                    ? response.data.interactiveToAddresses.split(",")
                    : [];
                const originalToAddresses = response.data.originalToAddresses
                    ? response.data.originalToAddresses.split(",")
                    : [];
                const ccAddresses = response.data.interactiveCcAddresses
                    ? response.data.interactiveCcAddresses.split(",")
                    : [];
                const originalCcAddresses = response.data.originalCcAddresses
                    ? response.data.originalCcAddresses.split(",")
                    : [];

                setOriginalTo(originalToAddresses);
                setOriginalCc(originalCcAddresses);
                setToAddresses(toAddresses);
                setCcAddresses(ccAddresses);
            } else {
                message.error(
                    t(
                        SEND_ID_EMAIL.FETCH_RECIPIENTS_FAIL.key,
                        SEND_ID_EMAIL.FETCH_RECIPIENTS_FAIL.value,
                    )
                );
            }
        } catch (error) {
            message.error(
                t(
                    SEND_ID_EMAIL.FETCH_RECIPIENTS_FAIL.key,
                    SEND_ID_EMAIL.FETCH_RECIPIENTS_FAIL.value,
                )
            );
            console.error("Error fetching email addresses:", error);
        } finally {
            setGlobalLoading(false);
        }
    };

    const updateEmailAddresses = async (emailAddresses, type, remove) => {
        let payload = {
            interactiveToAddresses: type === "to" ? emailAddresses : toAddresses.join(),
            interactiveCcAddresses: type === "cc" ? emailAddresses : ccAddresses.join(),
        };
        try {
            const response = await updateRecipients(payload, docId);
            if (response.statusCode === 5508) {
                return response;
            } else {
                const failKey = remove
                    ? SEND_ID_EMAIL.DELETE_ADDRESS_FAIL
                    : SEND_ID_EMAIL.UPDATE_RECIPIENTS_FAIL;
                message.error(t(failKey.key, failKey.value));
            }
        } catch (error) {
            console.error("Error updating email addresses:", error);
            const failKey = remove
                ? SEND_ID_EMAIL.DELETE_ADDRESS_FAIL
                : SEND_ID_EMAIL.UPDATE_RECIPIENTS_FAIL;
            message.error(t(failKey.key, failKey.value));
        }
    };

    const sendEmail = async () => {
        setGlobalLoading(true);
        try {
            const response = await sendInteractiveEmail(docId);
            if (response.statusCode === 5510) {
                setShowTransacModal(true);
                return response;
            } else {
                message.error(
                    t(SEND_ID_EMAIL.SEND_EMAIL_FAIL.key, SEND_ID_EMAIL.SEND_EMAIL_FAIL.value)
                );
            }
        } catch (error) {
            message.error(
                t(SEND_ID_EMAIL.SEND_EMAIL_FAIL.key, SEND_ID_EMAIL.SEND_EMAIL_FAIL.value)
            );
            console.error("Error sending email:", error);
        } finally {
            setGlobalLoading(false);
            onConfirm();
        }
    };

    useEffect(() => {
        if (visible) {
            setGlobalLoading(true);
            fetchEmailAddresses();
        }
    }, [visible, docId]);

    // --- TO handlers ---
    const handleAddTo = async (email) => {
        setLoadingTo(true);
        const newEmail = email.toLowerCase();
        if(!toAddresses.some(toMail => toMail.toLowerCase() === newEmail) && !originalTo.some(originalMail => originalMail.toLowerCase() === newEmail)) {
            const joinedAddresses = [...toAddresses, email].join();
            const res = await updateEmailAddresses(joinedAddresses, 'to');
            if (res?.statusCode === 5508) setToAddresses([...toAddresses, email]);
            setLoadingTo(false);
        } else {
            setLoadingTo(false);
            message.error(t(SEND_EMAIL_MODAL.EMAIL_ALREADY_EXISTS_ERROR.key, SEND_EMAIL_MODAL.EMAIL_ALREADY_EXISTS_ERROR.value));
        }
    };

    const handleRemoveTo = async (email) => {
        setLoadingTo(true);
        const joinedAddresses = toAddresses.filter((e) => e !== email).join();
        const res = await updateEmailAddresses(joinedAddresses, 'to', true);
        if (res?.statusCode === 5508)
            setToAddresses(toAddresses.filter((e) => e !== email));
        setLoadingTo(false);
    };

    // --- CC handlers ---
    const handleAddCc = async (email) => {
        setLoadingCc(true);
        const newEmail = email.toLowerCase();
        if(!ccAddresses.some(ccMail => ccMail.toLowerCase() === newEmail) && !originalCc.some(originalMail => originalMail.toLowerCase() === newEmail)) {
            const joinedAddresses = [...ccAddresses, email].join(",");
            const res = await updateEmailAddresses(joinedAddresses, 'cc');
            if (res?.statusCode === 5508) setCcAddresses([...ccAddresses, email]);
            setLoadingCc(false);
        } else {
            setLoadingCc(false);
            message.error(t(SEND_EMAIL_MODAL.EMAIL_ALREADY_EXISTS_ERROR.key, SEND_EMAIL_MODAL.EMAIL_ALREADY_EXISTS_ERROR.value));
        }
    };

    const handleRemoveCc = async (email) => {
        setLoadingCc(true);
        const joinedAddresses = ccAddresses.filter((e) => e !== email).join(",");
        const res = await updateEmailAddresses(joinedAddresses, 'cc', true);
        if (res?.statusCode === 5508)
            setCcAddresses(ccAddresses.filter((e) => e !== email));
        setLoadingCc(false);
    };

    const handleSendEmail = () => setConfirmVisible(true);

    const handleConfirmSend = async () => {
        await sendEmail();
    };

    const handleClose = () => {
        if (embedState?.isEmbedded) {
            const transactionEvent = {
                type: PdsClientEventName.CLIENT_INTERACTIVE_DOCUMENT_TRANSACTION_COMPLETE,
                data: ''
            }
            parentWindow.postMessage(transactionEvent, '*');
        }
        else {
            setShowTransacModal(false);
            history.push(`/interactiveTemplates`)
        }
    };

    return (
        <>
            <StyledModal
                title={t(
                    SEND_ID_EMAIL.SEND_EMAIL_TITLE.key,
                    SEND_ID_EMAIL.SEND_EMAIL_TITLE.value,
                )}
                visible={visible}
                onCancel={onCancel}
                footer={[
                    <Button key="cancel" onClick={onCancel}>
                        {t(SEND_ID_EMAIL.CANCEL_BTN.key, SEND_ID_EMAIL.CANCEL_BTN.value)}
                    </Button>,
                    <Button
                        key="send"
                        type="primary"
                        loading={globalLoading}
                        onClick={handleSendEmail}
                    >
                        {t(SEND_ID_EMAIL.SEND_EMAIL_BTN.key, SEND_ID_EMAIL.SEND_EMAIL_BTN.value)}
                    </Button>,
                ]}
                width={600}
                centered
            >
                <TagListInput
                    title={t(
                        SEND_ID_EMAIL.SEND_EMAIL_TO_TEXT.key,
                        SEND_ID_EMAIL.SEND_EMAIL_TO_TEXT.value,
                    )}
                    tags={toAddresses}
                    originalTags={originalTo}
                    onAdd={handleAddTo}
                    onRemove={handleRemoveTo}
                    loading={loadingTo}
                />
                <TagListInput
                    title="Cc"
                    tags={ccAddresses}
                    originalTags={originalCc}
                    onAdd={handleAddCc}
                    onRemove={handleRemoveCc}
                    loading={loadingCc}
                />
            </StyledModal>

            {confirmVisible && (
                <ConfirmationModal
                    showImage={false}
                    visibility={confirmVisible}
                    onCancel={() => setConfirmVisible(false)}
                    onConfirm={() => handleConfirmSend()}
                    confirmButtonText={t(
                        COMPLETE_TRANSACTION.CONFIRM_BUTTON.key,
                        COMPLETE_TRANSACTION.CONFIRM_BUTTON.value,
                    )}
                    confirmationText={t(
                        SEND_ID_EMAIL.CONFIRM_MODAL_DESCRIPTION.key,
                        SEND_ID_EMAIL.CONFIRM_MODAL_DESCRIPTION.value,
                    )}
                    isLoading={globalLoading}
                />
            )}

            {showTransacModal && (
                <TransactionCompleteModal
                    visible={showTransacModal}
                    onClose={() => handleClose()}
                    email={true}
                />
            )}
        </>
    );
};

export default SendIdEmail;
